import React, { useEffect, useMemo, useState } from 'react';
import { initializeIcons } from '@fluentui/font-icons-mdl2';
import { getIconClassName } from '@fluentui/style-utilities';
import { Button, Input, Message, Placeholder, Tooltip, Whisper } from 'rsuite';
import { Button as NsiButton, Modal } from '@nsi-advisory-services/components';
import {
  useCreateDocumentLibraryFolder,
  useDocumentLibraryContext,
  useDocumentLibraryItemContent,
  useDocumentLibraryItems,
  useUploadDocumentLibraryItem,
} from '../../hooks/useQueries';
import './DocumentLibraryBrowser.css';

initializeIcons();
const sharePointLogoIconClass = getIconClassName('SharepointLogo');

const normalizePath = (value) => String(value || '').replace(/\\/g, '/').trim().replace(/^\/+|\/+$/g, '');

const splitPath = (value) => {
  const path = normalizePath(value);
  return path ? path.split('/').filter(Boolean) : [];
};

const formatBytes = (value) => {
  const size = Number(value);
  if (!Number.isFinite(size) || size <= 0) return '-';
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  if (size < 1024 * 1024 * 1024) return `${(size / (1024 * 1024)).toFixed(2)} MB`;
  return `${(size / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

const formatDateTime = (value) => {
  if (!value) return '-';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const triggerBlobPreview = (blob) => {
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank', 'noopener,noreferrer');
  setTimeout(() => URL.revokeObjectURL(url), 60 * 1000);
};

const getFileExtension = (name) => {
  const value = String(name || '').trim();
  const dot = value.lastIndexOf('.');
  if (dot <= 0 || dot === value.length - 1) return '';
  return value.slice(dot + 1).toLowerCase();
};

const getItemIcon = (item) => {
  if (item?.kind === 'folder') return 'bi bi-folder-fill text-warning';

  const ext = getFileExtension(item?.name);
  const mime = String(item?.mimeType || '').toLowerCase();

  if (mime.includes('pdf') || ext === 'pdf') return 'bi bi-file-earmark-pdf-fill text-danger';
  if (mime.includes('word') || ['doc', 'docx'].includes(ext)) return 'bi bi-file-earmark-word-fill text-primary';
  if (mime.includes('excel') || ['xls', 'xlsx', 'csv'].includes(ext)) return 'bi bi-file-earmark-excel-fill text-success';
  if (mime.includes('powerpoint') || ['ppt', 'pptx'].includes(ext)) return 'bi bi-file-earmark-ppt-fill text-warning';
  if (mime.startsWith('image/') || ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg'].includes(ext)) {
    return 'bi bi-file-earmark-image-fill text-info';
  }
  if (mime.startsWith('video/') || ['mp4', 'mov', 'avi', 'mkv'].includes(ext)) return 'bi bi-file-earmark-play-fill text-primary';
  if (mime.startsWith('audio/') || ['mp3', 'wav', 'ogg'].includes(ext)) return 'bi bi-file-earmark-music-fill text-primary';
  if (['zip', 'rar', '7z', 'tar', 'gz'].includes(ext)) return 'bi bi-file-earmark-zip-fill text-secondary';
  return 'bi bi-file-earmark-fill text-muted';
};

const isNoAccessError = (error) => {
  const message = String(error?.message || error || '').toLowerCase();
  return message.includes('access denied') || message.includes('forbidden') || message.includes('403');
};

export default function DocumentLibraryBrowser({ employeeId = 'me' }) {
  const normalizedEmployeeId = String(employeeId || 'me');

  const [currentPath, setCurrentPath] = useState('');
  const [newFolderName, setNewFolderName] = useState('');
  const [createError, setCreateError] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const [createFolderModalOpen, setCreateFolderModalOpen] = useState(false);
  const [isDragOverCurrentFolder, setIsDragOverCurrentFolder] = useState(false);
  const [dragOverFolderId, setDragOverFolderId] = useState(null);

  const contextQuery = useDocumentLibraryContext(normalizedEmployeeId, { enabled: true });
  const itemsQuery = useDocumentLibraryItems(normalizedEmployeeId, currentPath, {
    enabled: Boolean(contextQuery.data?.drive?.id),
  });
  const createFolderMutation = useCreateDocumentLibraryFolder();
  const uploadDocumentLibraryItemMutation = useUploadDocumentLibraryItem();
  const itemContentMutation = useDocumentLibraryItemContent();

  useEffect(() => {
    setCurrentPath('');
    setNewFolderName('');
    setCreateError(null);
    setUploadError(null);
    setCreateFolderModalOpen(false);
    setIsDragOverCurrentFolder(false);
    setDragOverFolderId(null);
  }, [normalizedEmployeeId]);

  const breadcrumbs = splitPath(currentPath);
  const items = useMemo(() => itemsQuery.data?.items || [], [itemsQuery.data]);

  const navigateUp = () => {
    const parts = splitPath(currentPath);
    parts.pop();
    setCurrentPath(parts.join('/'));
  };

  const navigateToFolder = (name) => {
    const cleanName = String(name || '').trim();
    if (!cleanName) return;
    const nextPath = normalizePath(currentPath ? `${currentPath}/${cleanName}` : cleanName);
    setCurrentPath(nextPath);
  };

  const navigateToCrumbIndex = (index) => {
    const parts = splitPath(currentPath);
    if (index < 0) {
      setCurrentPath('');
      return;
    }
    setCurrentPath(parts.slice(0, index + 1).join('/'));
  };

  const createFolder = async () => {
    const folderName = String(newFolderName || '').trim();
    if (!folderName) {
      setCreateError('Folder name is required');
      return;
    }
    setCreateError(null);
    try {
      await createFolderMutation.mutateAsync({
        employeeId: normalizedEmployeeId,
        path: currentPath,
        folderName,
      });
      setCreateFolderModalOpen(false);
      await itemsQuery.refetch();
    } catch (err) {
      setCreateError(String(err?.message || err));
    }
  };

  const openCreateFolderModal = () => {
    setCreateError(null);
    setNewFolderName('');
    setCreateFolderModalOpen(true);
  };

  const closeCreateFolderModal = () => setCreateFolderModalOpen(false);

  // Cleared once the dialog has faded out, so the name doesn't vanish while it is still visible.
  const resetCreateFolderForm = () => {
    setCreateError(null);
    setNewFolderName('');
  };

  const uploadFilesToPath = async (files, targetPath) => {
    const list = Array.from(files || []);
    if (list.length === 0) return;

    setUploadError(null);

    try {
      for (const file of list) {
        await uploadDocumentLibraryItemMutation.mutateAsync({
          employeeId: normalizedEmployeeId,
          path: targetPath,
          file,
        });
      }
      await itemsQuery.refetch();
    } catch (err) {
      setUploadError(String(err?.message || err));
    }
  };

  const handleCurrentFolderDragOver = (event) => {
    event.preventDefault();
    if (uploadDocumentLibraryItemMutation.isPending) return;
    setIsDragOverCurrentFolder(true);
    setDragOverFolderId(null);
  };

  const handleCurrentFolderDragLeave = (event) => {
    event.preventDefault();
    if (!event.currentTarget.contains(event.relatedTarget)) {
      setIsDragOverCurrentFolder(false);
    }
  };

  const handleCurrentFolderDrop = async (event) => {
    event.preventDefault();
    setIsDragOverCurrentFolder(false);
    setDragOverFolderId(null);
    if (uploadDocumentLibraryItemMutation.isPending) return;
    await uploadFilesToPath(event.dataTransfer?.files, currentPath);
  };

  const handleFolderDragOver = (event, folderId) => {
    event.preventDefault();
    event.stopPropagation();
    if (uploadDocumentLibraryItemMutation.isPending) return;
    setIsDragOverCurrentFolder(false);
    setDragOverFolderId(folderId);
  };

  const handleFolderDragLeave = (event, folderId) => {
    event.preventDefault();
    event.stopPropagation();
    if (!event.currentTarget.contains(event.relatedTarget) && dragOverFolderId === folderId) {
      setDragOverFolderId(null);
    }
  };

  const handleFolderDrop = async (event, folderName) => {
    event.preventDefault();
    event.stopPropagation();
    setDragOverFolderId(null);
    if (uploadDocumentLibraryItemMutation.isPending) return;
    const targetPath = normalizePath(currentPath ? `${currentPath}/${folderName}` : folderName);
    await uploadFilesToPath(event.dataTransfer?.files, targetPath);
  };

  const previewFile = async (item) => {
    if (!item?.id || item?.kind !== 'file') return;
    const result = await itemContentMutation.mutateAsync({
      employeeId: normalizedEmployeeId,
      itemId: item.id,
      fileName: item.name,
      download: false,
    });
    triggerBlobPreview(result.blob);
  };

  const openExternal = (url) => {
    const target = String(url || '').trim();
    if (!target) return;
    window.open(target, '_blank', 'noopener,noreferrer');
  };

  const iconButtonStyle = {
    border: '1px solid var(--rs-border-primary)',
    minWidth: 30,
    width: 30,
    height: 30,
    padding: 0,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  if (contextQuery.isLoading) {
    return <Placeholder.Paragraph rows={6} active />;
  }

  if (contextQuery.error) {
    if (isNoAccessError(contextQuery.error)) {
      return (
        <Message type="warning" showIcon>
          No access to this employee&apos;s document library.
        </Message>
      );
    }

    return (
      <Message type="error" showIcon header="Document library error">
        {String(contextQuery.error?.message || contextQuery.error)}
      </Message>
    );
  }

  const drive = contextQuery.data?.drive;

  if (!drive?.id) {
    return (
      <Message type="warning" showIcon>
        Document library is not configured for this employee.
      </Message>
    );
  }

  return (
    <div className="d-flex flex-column gap-2">
      <div className="d-flex align-items-center gap-1 flex-wrap document-library-breadcrumbs">
        <Button size="xs" appearance="subtle" onClick={navigateUp} disabled={!currentPath}>
          <i className="bi bi-arrow-left me-1" />
          Back
        </Button>
        <Button size="xs" appearance="link" onClick={() => navigateToCrumbIndex(-1)} style={{ padding: 0 }}>
          Library
        </Button>
        {breadcrumbs.map((part, index) => (
          <React.Fragment key={`${part}-${index}`}>
            <span className="text-muted">/</span>
            <Button size="xs" appearance="link" onClick={() => navigateToCrumbIndex(index)} style={{ padding: 0 }}>
              {part}
            </Button>
          </React.Fragment>
        ))}

        <div className="d-flex align-items-center gap-1 ms-auto">
          <Whisper
            placement="top"
            trigger="hover"
            speaker={<Tooltip>Create folder</Tooltip>}
          >
            <Button
              size="xs"
              appearance="subtle"
              onClick={openCreateFolderModal}
              style={iconButtonStyle}
              aria-label="Create folder"
            >
              <i className="bi bi-folder-plus" />
            </Button>
          </Whisper>
          <Whisper
            placement="top"
            trigger="hover"
            speaker={<Tooltip>View in SharePoint</Tooltip>}
          >
            <Button
              size="xs"
              appearance="subtle"
              onClick={() => openExternal(drive.webUrl)}
              disabled={!drive.webUrl}
              style={iconButtonStyle}
              aria-label="View in SharePoint"
            >
              <i className={sharePointLogoIconClass} />
            </Button>
          </Whisper>
        </div>
      </div>

      <div
        className={`document-library-current-drop-target ${isDragOverCurrentFolder ? 'document-library-current-drop-target--active' : ''}`}
        onDragEnter={handleCurrentFolderDragOver}
        onDragOver={handleCurrentFolderDragOver}
        onDragLeave={handleCurrentFolderDragLeave}
        onDrop={handleCurrentFolderDrop}
      >
        <i className="bi bi-cloud-arrow-up" aria-hidden="true" />
        <span>
          {uploadDocumentLibraryItemMutation.isPending
            ? 'Uploading...'
            : `Drop files here to upload to ${currentPath ? `/${currentPath}` : 'the current folder'}`}
        </span>
      </div>

      {uploadError ? (
        <Message type="error" showIcon>{uploadError}</Message>
      ) : null}

      {itemsQuery.isLoading ? <Placeholder.Paragraph rows={5} active /> : null}

      {itemsQuery.error ? (
        isNoAccessError(itemsQuery.error) ? (
          <Message type="warning" showIcon>
            No access to this employee&apos;s document library.
          </Message>
        ) : (
          <Message type="error" showIcon>
            {String(itemsQuery.error?.message || itemsQuery.error)}
          </Message>
        )
      ) : null}

      {!itemsQuery.isLoading ? (
        <div className="d-flex flex-column gap-1" style={{ maxHeight: 360, overflowY: 'auto' }}>
          {items.length === 0 ? (
            <div className="text-muted">This folder is empty.</div>
          ) : (
            items.map((item) => (
              <div
                key={item.id}
                className={`document-library-item border rounded p-2 d-flex justify-content-between align-items-center gap-2 flex-nowrap ${item.kind === 'folder' ? 'document-library-item-folder' : ''} ${item.kind === 'folder' && dragOverFolderId === item.id ? 'document-library-drop-folder-target' : ''}`}
                role={item.kind === 'folder' ? 'button' : undefined}
                tabIndex={item.kind === 'folder' ? 0 : undefined}
                onClick={item.kind === 'folder' ? () => navigateToFolder(item.name) : undefined}
                onKeyDown={item.kind === 'folder' ? (event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    navigateToFolder(item.name);
                  }
                } : undefined}
                onDragEnter={item.kind === 'folder' ? (event) => handleFolderDragOver(event, item.id) : undefined}
                onDragOver={item.kind === 'folder' ? (event) => handleFolderDragOver(event, item.id) : undefined}
                onDragLeave={item.kind === 'folder' ? (event) => handleFolderDragLeave(event, item.id) : undefined}
                onDrop={item.kind === 'folder' ? (event) => handleFolderDrop(event, item.name) : undefined}
              >
                <div className="d-flex align-items-center gap-2 flex-grow-1" style={{ minWidth: 0 }}>
                  <i className={getItemIcon(item)} aria-hidden="true" style={{ fontSize: 18 }} />
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div className="fw-semibold text-truncate" title={item.name}>
                      {item.name}
                    </div>
                    <div className="text-muted" style={{ fontSize: 12 }}>
                      {item.kind === 'folder' ? 'Folder' : formatBytes(item.size)} • {formatDateTime(item.lastModifiedDateTime)}
                    </div>
                    {item.kind === 'folder' && dragOverFolderId === item.id ? (
                      <div className="document-library-drop-hint">Drop to upload into this folder</div>
                    ) : null}
                  </div>
                  {item.kind === 'folder' ? <i className="bi bi-chevron-right text-muted" aria-hidden="true" /> : null}
                </div>

                <div className="d-flex gap-1 ms-auto align-items-center" style={{ flexShrink: 0 }}>
                  {item.kind !== 'folder' ? (
                    <Whisper
                      placement="top"
                      trigger="hover"
                      speaker={<Tooltip>Preview</Tooltip>}
                    >
                      <Button
                        size="xs"
                        appearance="subtle"
                        loading={itemContentMutation.isPending}
                        onClick={(event) => {
                          event?.stopPropagation?.();
                          previewFile(item);
                        }}
                        style={iconButtonStyle}
                        aria-label="Preview"
                      >
                        <i className="bi bi-eye" />
                      </Button>
                    </Whisper>
                  ) : null}

                  <Whisper
                    placement="top"
                    trigger="hover"
                    speaker={<Tooltip>View in SharePoint</Tooltip>}
                  >
                    <Button
                      size="xs"
                      appearance="subtle"
                      disabled={!item.webUrl}
                      onClick={(event) => {
                        event?.stopPropagation?.();
                        openExternal(item.webUrl);
                      }}
                      style={iconButtonStyle}
                      aria-label="View in SharePoint"
                    >
                      <i className={sharePointLogoIconClass} />
                    </Button>
                  </Whisper>
                </div>
              </div>
            ))
          )}
        </div>
      ) : null}

      <Modal open={createFolderModalOpen} onClose={closeCreateFolderModal} onExited={resetCreateFolderForm} size="xs">
        <Modal.Header>
          <Modal.Title>Create Folder</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Input
            value={newFolderName}
            onChange={setNewFolderName}
            onPressEnter={createFolder}
            placeholder="Folder name"
            disabled={createFolderMutation.isPending}
          />
          {createError ? (
            <Message type="error" showIcon style={{ marginTop: 10 }}>{createError}</Message>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <NsiButton onClick={closeCreateFolderModal}>Cancel</NsiButton>
          <NsiButton
            variant="primary"
            onClick={createFolder}
            loading={createFolderMutation.isPending}
            disabled={!String(newFolderName || '').trim()}
          >
            Create
          </NsiButton>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
